import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFilesByExtension } from '@/entities/knowledge-base';

const CAMPAIGN_EXTENSION = '.campaign';

interface CampaignFile {
  fileId: string;
  fileName: string;
}

interface UseCampaignFilesResult {
  campaignFiles: CampaignFile[];
  isLoading: boolean;
}

/**
 * Lists every `.campaign` file across the currently-selected knowledge bases (server-side filter),
 * for the Templates agent's Campaign picker. Re-fetches when the selected KB set changes.
 */
export function useCampaignFiles(knowledgeBaseIds: string[]): UseCampaignFilesResult {
  // Stable, order-independent cache key for the selected KB set.
  const sortedIds = useMemo(() => [...knowledgeBaseIds].sort(), [knowledgeBaseIds]);

  const { data: files = [], isLoading } = useQuery({
    queryKey: ['campaignFiles', sortedIds],
    queryFn: () => getFilesByExtension(sortedIds, CAMPAIGN_EXTENSION),
    enabled: sortedIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const campaignFiles: CampaignFile[] = useMemo(
    () =>
      files
        .map((f) => ({ fileId: f.id, fileName: f.fileName }))
        .sort((a, b) => a.fileName.localeCompare(b.fileName)),
    [files],
  );

  return { campaignFiles, isLoading };
}
